import { QRScannerDialog, QRScannerDialogRef } from '@/components/QRScannerDialog'
import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { SearchableSelect, type SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { useAssignBed, useGetBeds } from '@/features/beds/hooks/useBed'
import { Bed } from '@/features/beds/types/bed'
import { useGetDepartmentOptions } from '@/features/departments/hooks/useDepartment'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useConfirm } from '@/hooks/useConfirm'
import { formatBuildingFloorLocation } from '@/utils/location'
import { getMutationOptions } from '@/utils/mutation'
import { showError } from '@/utils/toast'
import { ScanLine } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function BedToPatientSection({
  patientId,
  departmentId,
  bed,
}: {
  patientId: string
  departmentId?: string
  bed?: Bed
}) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const scannerRef = useRef<QRScannerDialogRef>(null)

  const assignPatient = useAssignBed()
  const { data: configs } = useGetConfigs()

  const [form, setForm] = useState({ departmentId: '', bedId: '' })
  const { data: departments = [], isFetching: isFetchingDepartments } = useGetDepartmentOptions(undefined, !bed)
  const { data: beds = [], isFetching: isFetchingBeds } = useGetBeds(form.departmentId, !!form.departmentId)

  const filteredBeds = beds.filter(p => !p.patientId)
  const warehouseDepartmentId = configs?.[AppConfigKey.WarehouseDepartmentId]
  const departmentGroups = useMemo<SearchableSelectGroup[]>(() => {
    const departmentOptions =
      departments?.map(department => ({
        value: department.id,
        label: department.name,
        description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
        searchValue: `${department.name} ${department.buildingName || ''} ${department.floorName || ''}`,
      })) ?? []

    return [
      {
        label: t('scans.mainWarehouse'),
        options: departmentOptions.filter(department => department.value === warehouseDepartmentId),
      },
      {
        label: t('scans.otherDepartments'),
        options: departmentOptions.filter(department => department.value !== warehouseDepartmentId),
      },
    ]
  }, [departments, t, warehouseDepartmentId])

  const hasBed = !!bed
  const isInvalid = !form.bedId || !form.departmentId || !patientId || hasBed

  const handleScan = useCallback(
    (text: string) => {
      try {
        const url = new URL(text)
        const match = url.pathname.match(/\/scan\/bed\/([^/]+)\/([^/]+)/)
        const departmentId = match?.[1]
        const bedId = match?.[2]

        if (!departmentId || !bedId) {
          showError(t('common.qrScanner.invalidQr'))
          return
        }

        const department = departments.some(dept => dept.id === departmentId)
        if (!department) {
          showError(t('common.qrScanner.departmentNotFound'))
          return
        }

        setForm({
          departmentId,
          bedId: bedId,
        })
      } catch {
        showError(t('common.qrScanner.invalidQr'))
      }
    },
    [t],
  )

  useEffect(() => {
    if (!form.bedId || !filteredBeds) return

    const exists = filteredBeds.some(bed => bed.id === form.bedId)

    if (!exists) {
      showError(t('common.qrScanner.bedNotFound'))

      setForm(prev => ({
        ...prev,
        bedId: '',
      }))
    }
  }, [filteredBeds, form.bedId, t])

  function handleAssignPatient() {
    if (isInvalid) return
    const options = getMutationOptions({
      successMessage: t('patient.toast.assignSuccess'),
      onSuccess: () => setForm({ departmentId: '', bedId: '' }),
    })
    assignPatient.mutate({ departmentId: form.departmentId, id: form.bedId, payload: { patientId } }, options)
  }

  async function handleDisChargePatient() {
    if (!bed || !departmentId) return
    console.log(bed)
    const confirmed = await confirm({
      variant: 'destructive',
      title: `${t('patient.detail.bedSection.confirmDischargeTitle')}`,
      description: `${t('patient.detail.bedSection.confirmDischargeDescription', { name: bed?.patientName })}`,
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('patient.toast.dischargeSuccess'),
      })
      const payload = {
        patientId: null,
      }
      assignPatient.mutate({ departmentId: departmentId, id: bed.id, payload: payload }, options)
    }
  }
  return (
    <>
      <SectionCard
        title={t('patient.detail.manageBed')}
        cardClass="mt-4"
        headerClass="pb-0"
        right={
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => scannerRef.current?.start()}
            disabled={isFetchingDepartments || isFetchingBeds}
            title={t('common.qrScanner.scanButton')}
          >
            <ScanLine className="size-4" />
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {hasBed ? (
            <>
              <Button
                variant="destructive"
                onClick={handleDisChargePatient}
                className="text-xs"
                disabled={!hasBed || !departmentId}
                data-testid="patient-detail-discharge-button"
              >
                {assignPatient.isPending && <Spinner />}
                {t('common.discharge')}
              </Button>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">{t('common.department')} *</Label>
                  <SearchableSelect
                    value={form.departmentId}
                    onValueChange={departmentId => setForm({ ...form, departmentId, bedId: '' })}
                    groups={departmentGroups}
                    placeholder={t('common.placeholder.selectDepartment')}
                    searchPlaceholder={t('common.placeholder.searchDepartment')}
                    emptyText={t('common.noDepartment')}
                    disabled={isFetchingDepartments}
                    triggerClassName="bg-secondary/50 text-xs"
                    testId="patient-detail-department-select"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">{t('common.bed')} *</Label>
                  <Select
                    value={form.bedId}
                    onValueChange={v => setForm({ ...form, bedId: v })}
                    disabled={!form.departmentId || isFetchingBeds || !filteredBeds?.length}
                  >
                    <SelectTrigger className="h-9 bg-secondary/50 w-full" data-testid="patient-detail-bed-select">
                      <SelectValue
                        placeholder={
                          !form.departmentId
                            ? t('common.placeholder.selectDepartmentFirst')
                            : isFetchingBeds
                              ? t('common.loading')
                              : !filteredBeds?.length
                                ? t('common.placeholder.noBed')
                                : t('common.placeholder.selectBed')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-card text-foreground">
                      {filteredBeds?.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleAssignPatient} className="text-xs" disabled={isInvalid} data-testid="patient-detail-add-bed-button">
                {assignPatient.isPending && <Spinner />}
                {t('common.add')}
              </Button>
            </>
          )}
        </div>
      </SectionCard>

      <QRScannerDialog ref={scannerRef} onScan={handleScan} />
    </>
  )
}
